import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Alert,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
} from '@mui/x-data-grid';
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  ArrowForward as ArrowForwardIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AdminJob, WorkflowState, CompletionStatus, JobSearchFilters } from '../types';
import { adminApiService } from '../services/api';
import { Layout } from '../components/Layout';
import { useAuth } from '../auth/AuthProvider';

const workflowStateColors: Record<WorkflowState, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  started: 'info',
  submitted: 'success',
  processed: 'warning',
  completed: 'secondary',
};

const completionStatusColors: Record<CompletionStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  uploaded: 'secondary',
  upload_failed: 'error',
  canceled: 'default',
};

const workflowStateLabels: Record<WorkflowState, string> = {
  started: 'Started',
  submitted: 'Submitted',
  processed: 'Processed',
  completed: 'Completed',
};

const completionStatusLabels: Record<CompletionStatus, string> = {
  uploaded: 'Uploaded',
  upload_failed: 'Upload Failed',
  canceled: 'Canceled',
};

const INTERACTION_COOLDOWN = 2 * 60 * 1000; // 2 minutes

export const Jobs: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, accessToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState<AdminJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [directLookup, setDirectLookup] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  // Initialize filters from URL parameters
  const getInitialFilters = (): JobSearchFilters => {
    const filters: JobSearchFilters = {};

    const workflowState = searchParams.get('workflowState');
    if (workflowState) {
      filters.workflowState = [workflowState as WorkflowState];
    }

    const completionStatus = searchParams.get('completionStatus');
    if (completionStatus) {
      filters.completionStatus = [completionStatus as CompletionStatus];
    }

    const jobNumber = searchParams.get('jobNumber');
    if (jobNumber) {
      filters.jobNumber = jobNumber;
    }

    const reporterName = searchParams.get('reporterName');
    if (reporterName) {
      filters.reporterName = reporterName;
    }

    return filters;
  };

  // Track when the user last interacted with filters or pagination
  // to delay silent polling after interaction
  const lastInteractionRef = useRef<number>(0);

  // Filters
  const [filters, setFilters] = useState<JobSearchFilters>(getInitialFilters());
  const [showFilters, setShowFilters] = useState(() => {
    // Show filters panel if there are URL parameters
    return searchParams.has('workflowState') || searchParams.has('completionStatus') ||
           searchParams.has('jobNumber') || searchParams.has('reporterName');
  });

  const loadJobs = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      console.log('Loading jobs with filters:', filters);
      const response = await adminApiService.getJobs(
        paginationModel.page + 1,
        paginationModel.pageSize,
        filters
      );

      if (response.success) {
        setJobs(response.data);
        setTotalCount(response.pagination.totalCount);
        setDirectLookup(response.directLookup || false);

        // If the current page is now beyond the last page, reset to page 1
        const totalPages = Math.ceil(response.pagination.totalCount / paginationModel.pageSize);
        if (paginationModel.page > 0 && paginationModel.page >= totalPages) {
          setPaginationModel(prev => ({ ...prev, page: 0 }));
        }
      } else {
        if (!silent) {
          setError('Failed to load jobs');
        }
      }
    } catch (err) {
      if (!silent) {
        setError('An error occurred while loading jobs');
      }
      console.error('Jobs loading error:', err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [paginationModel, filters]);

  useEffect(() => {
    // Only load jobs if authenticated and have a token
    if (isAuthenticated && accessToken) {
      loadJobs();
    }
  }, [isAuthenticated, accessToken, paginationModel, filters, loadJobs]);

  // Poll for updated job data every 60 seconds, but skip if the user
  // interacted with filters or pagination within the last 2 minutes
  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;

    const interval = setInterval(() => {
      const timeSinceInteraction = Date.now() - lastInteractionRef.current;
      if (timeSinceInteraction >= INTERACTION_COOLDOWN) {
        loadJobs(true);
      }
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, accessToken, loadJobs]);

  const handleViewJob = (jobNumber: string) => {
    navigate(`/jobs/${jobNumber}`);
  };

  // Update URL parameters when filters change
  const updateUrlParams = (newFilters: JobSearchFilters) => {
    const params = new URLSearchParams();
    
    if (newFilters.workflowState?.length) {
      params.set('workflowState', newFilters.workflowState.join(','));
    }
    
    if (newFilters.completionStatus?.length) {
      params.set('completionStatus', newFilters.completionStatus.join(','));
    }
    
    // Add other filters if needed
    if (newFilters.jobNumber) {
      params.set('jobNumber', newFilters.jobNumber);
    }
    
    if (newFilters.reporterName) {
      params.set('reporterName', newFilters.reporterName);
    }
    
    // Use replace to avoid adding to browser history for every filter change
    setSearchParams(params, { replace: true });
  };

  const handleFilterChange = (field: keyof JobSearchFilters, value: any) => {
    lastInteractionRef.current = Date.now();
    const newFilters = {
      ...filters,
      [field]: value,
    };
    setFilters(newFilters);
    updateUrlParams(newFilters);
  };

  const clearFilters = () => {
    const newFilters = {};
    setFilters(newFilters);
    updateUrlParams(newFilters);
  };

  const columns: GridColDef[] = [
    {
      field: 'jobNumber',
      headerName: 'Job Number',
      width: 120,
      renderCell: (params) => (
        <Button
          variant="text"
          onClick={() => handleViewJob(params.value)}
          sx={{ textTransform: 'none' }}
        >
          {params.value}
        </Button>
      ),
    },
    {
      field: 'jobDate',
      headerName: 'Job Date',
      width: 120,
      valueFormatter: (params) => new Date(params.value + 'T12:00:00').toLocaleDateString(),
    },
    {
      field: 'deponentName',
      headerName: 'Deponent',
      width: 180,
    },
    {
      field: 'caseName',
      headerName: 'Case Name',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: 'firmName',
      headerName: 'Firm',
      width: 180,
    },
    {
      field: 'reporterName',
      headerName: 'Reporter',
      width: 150,
    },
    {
      field: 'workflowState',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => {
        const workflowState = params.value as WorkflowState;
        const completionStatus = params.row.completionStatus as CompletionStatus | undefined;
        
        if (workflowState === 'completed' && completionStatus) {
          // Show completion status with custom styling for purple
          const isUploaded = completionStatus === 'uploaded';
          const isFailed = completionStatus === 'upload_failed';
          const isCanceled = completionStatus === 'canceled';
          
          return (
            <Chip
              label={completionStatusLabels[completionStatus]}
              size="small"
              sx={{
                backgroundColor: isUploaded ? '#9c27b0' : 
                               isFailed ? '#f44336' : 
                               isCanceled ? '#757575' : '#9c27b0',
                color: 'white',
                border: `2px solid ${isUploaded ? '#9c27b0' : 
                                   isFailed ? '#f44336' : 
                                   isCanceled ? '#757575' : '#9c27b0'}`,
                fontWeight: !isUploaded ? 'bold' : 'normal',
                '&:hover': {
                  backgroundColor: isUploaded ? '#7b1fa2' : 
                                 isFailed ? '#d32f2f' : 
                                 isCanceled ? '#616161' : '#7b1fa2',
                }
              }}
            />
          );
        }
        
        // Handle completed workflow state with custom purple styling
        if (workflowState === 'completed') {
          return (
            <Chip
              label={workflowStateLabels[workflowState]}
              size="small"
              sx={{
                backgroundColor: '#9c27b0',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#7b1fa2',
                }
              }}
            />
          );
        }
        
        return (
          <Chip
            label={workflowStateLabels[workflowState]}
            color={workflowStateColors[workflowState]}
            size="small"
          />
        );
      },
    },
    {
      field: 'lastSubmitted',
      headerName: 'Last Submitted',
      width: 140,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString() : '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleViewJob(params.row.jobNumber)}
            title="View Details"
          >
            <ViewIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
              window.open(`${apiBaseUrl}/admin/jobs/${params.row.jobNumber}/worksheet/download`, '_blank');
            }}
            title="Download Worksheet"
          >
            <DownloadIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  // Calculate the time period being displayed (20 business days)
  const getDataPeriodDescription = () => {
    const today = new Date();
    const lookbackDate = new Date(today);
    let businessDaysBack = 0;
    
    while (businessDaysBack < 20) {
      lookbackDate.setDate(lookbackDate.getDate() - 1);
      // Skip weekends (Saturday = 6, Sunday = 0)
      if (lookbackDate.getDay() !== 0 && lookbackDate.getDay() !== 6) {
        businessDaysBack++;
      }
    }
    
    return {
      startDate: lookbackDate.toLocaleDateString(),
      endDate: today.toLocaleDateString(),
      businessDays: 20
    };
  };

  const dataPeriod = getDataPeriodDescription();

  // Handle workflow state filter from diagram
  const handleWorkflowStateFilter = (state: WorkflowState) => {
    lastInteractionRef.current = Date.now();
    const newFilters = { ...filters };
    newFilters.workflowState = [state];
    delete newFilters.completionStatus; // Remove completion status when filtering by workflow state

    console.log('Setting workflow state filter:', state, 'New filters:', newFilters);
    setFilters(newFilters);
    updateUrlParams(newFilters);
    // Show filters panel so user can see the applied filter
    setShowFilters(true);
  };

  // Handle completion status filter from diagram
  const handleCompletionStatusFilter = (status: CompletionStatus) => {
    lastInteractionRef.current = Date.now();
    const newFilters = {
      ...filters,
      workflowState: ['completed' as WorkflowState], // Set to completed when filtering by completion status
      completionStatus: [status]
    };
    setFilters(newFilters);
    updateUrlParams(newFilters);
    // Show filters panel so user can see the applied filter
    setShowFilters(true);
  };

  // Clear workflow state filter
  const clearWorkflowStateFilter = () => {
    setFilters(prev => ({
      ...prev,
      workflowState: undefined,
      completionStatus: undefined
    }));
  };

  // Check if completion status is filtered
  const isCompletionStatusFiltered = (status: CompletionStatus) => {
    return filters.completionStatus?.includes(status) ?? false;
  };

  // Workflow visualization component
  const WorkflowDiagram = () => {
    const workflowSteps = [
      {
        state: 'started' as WorkflowState,
        label: 'Started',
        description: 'A reporter has accessed the worksheet',
        color: workflowStateColors.started
      },
      {
        state: 'submitted' as WorkflowState,
        label: 'Submitted',
        description: 'The worksheet was submitted',
        color: workflowStateColors.submitted
      },
      {
        state: 'processed' as WorkflowState,
        label: 'Processed',
        description: 'Conversion and summarization phases completed',
        color: workflowStateColors.processed
      }
    ];

    const completionStatuses = [
      {
        status: 'uploaded' as CompletionStatus,
        label: 'Uploaded',
        description: 'Successfully uploaded to RB',
        color: completionStatusColors.uploaded
      },
      {
        status: 'upload_failed' as CompletionStatus,
        label: 'Upload Failed',
        description: 'Upload to RB failed',
        color: completionStatusColors.upload_failed
      },
      {
        status: 'canceled' as CompletionStatus,
        label: 'Canceled',
        description: 'Processing was canceled',
        color: completionStatusColors.canceled
      }
    ];

    const isStateFiltered = (state: WorkflowState) => {
      return filters.workflowState?.includes(state) ?? false;
    };

    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                Job Workflow Lifecycle
              </Typography>
            </Box>
            {(filters.workflowState || filters.completionStatus) && (
              <Button 
                size="small" 
                onClick={clearWorkflowStateFilter}
                color="secondary"
              >
                Clear Filter
              </Button>
            )}
          </Box>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap">
            {/* Regular workflow steps */}
            {workflowSteps.map((step, index) => (
              <React.Fragment key={step.state}>
                <Box 
                  display="flex" 
                  flexDirection="column" 
                  alignItems="center" 
                  sx={{ 
                    minWidth: 120, 
                    mb: { xs: 2, md: 0 },
                    cursor: 'pointer',
                    opacity: filters.workflowState && !isStateFiltered(step.state) ? 0.5 : 1,
                    transition: 'opacity 0.2s',
                    '&:hover': {
                      opacity: 1
                    }
                  }}
                  onClick={() => handleWorkflowStateFilter(step.state)}
                >
                  <Chip
                    label={step.label}
                    color={step.color}
                    variant={isStateFiltered(step.state) ? "filled" : "outlined"}
                    sx={{ 
                      mb: 1,
                      fontWeight: isStateFiltered(step.state) ? 'bold' : 'normal',
                      minWidth: 80,
                      border: isStateFiltered(step.state) ? 2 : 1,
                      '&:hover': {
                        transform: 'scale(1.05)'
                      },
                      transition: 'transform 0.2s'
                    }}
                  />
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    textAlign="center"
                    sx={{ maxWidth: 120 }}
                  >
                    {step.description}
                  </Typography>
                </Box>
                <Box 
                  display={{ xs: 'none', md: 'flex' }} 
                  alignItems="center" 
                  sx={{ mx: 1 }}
                >
                  <ArrowForwardIcon color="action" />
                </Box>
              </React.Fragment>
            ))}
            
            {/* Completed section with sub-statuses */}
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              sx={{ 
                minWidth: 180,
                mb: { xs: 2, md: 0 }
              }}
            >
              {/* Completed header */}
              <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center"
                sx={{ 
                  cursor: 'pointer',
                  opacity: filters.workflowState && !isStateFiltered('completed') && !filters.completionStatus ? 0.5 : 1,
                  transition: 'opacity 0.2s',
                  '&:hover': {
                    opacity: 1
                  },
                  mb: 1
                }}
                onClick={() => handleWorkflowStateFilter('completed')}
              >
                <Chip
                  label="Completed"
                  variant={isStateFiltered('completed') ? "filled" : "outlined"}
                  sx={{ 
                    fontWeight: isStateFiltered('completed') ? 'bold' : 'normal',
                    minWidth: 80,
                    border: isStateFiltered('completed') ? 2 : 1,
                    backgroundColor: isStateFiltered('completed') ? '#9c27b0' : 'transparent',
                    borderColor: '#9c27b0',
                    color: isStateFiltered('completed') ? 'white' : '#9c27b0',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      backgroundColor: isStateFiltered('completed') ? '#7b1fa2' : 'rgba(156, 39, 176, 0.08)',
                    },
                    transition: 'transform 0.2s'
                  }}
                />
              </Box>
              
              {/* Completion sub-statuses */}
              <Box display="flex" flexDirection="column" gap={0.5} alignItems="center">
                {completionStatuses.map((status) => (
                  <Box
                    key={status.status}
                    sx={{ 
                      cursor: 'pointer',
                      opacity: filters.completionStatus && !isCompletionStatusFiltered(status.status) ? 0.5 : 1,
                      transition: 'opacity 0.2s',
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                    onClick={() => handleCompletionStatusFilter(status.status)}
                  >
                    <Chip
                      label={status.label}
                      variant={isCompletionStatusFiltered(status.status) ? "filled" : "outlined"}
                      size="small"
                      sx={{ 
                        fontWeight: isCompletionStatusFiltered(status.status) ? 'bold' : 'normal',
                        minWidth: 70,
                        border: isCompletionStatusFiltered(status.status) ? 2 : 1,
                        backgroundColor: isCompletionStatusFiltered(status.status) ? 
                          (status.status === 'uploaded' ? '#9c27b0' :
                           status.status === 'upload_failed' ? '#f44336' :
                           status.status === 'canceled' ? '#757575' : 'transparent') : 'transparent',
                        borderColor: status.status === 'uploaded' ? '#9c27b0' :
                                    status.status === 'upload_failed' ? '#f44336' :
                                    status.status === 'canceled' ? '#757575' : undefined,
                        color: isCompletionStatusFiltered(status.status) ? 'white' :
                               (status.status === 'uploaded' ? '#9c27b0' :
                                status.status === 'upload_failed' ? '#f44336' :
                                status.status === 'canceled' ? '#757575' : 'inherit'),
                        '&:hover': {
                          transform: 'scale(1.05)',
                          backgroundColor: isCompletionStatusFiltered(status.status) ? 
                            (status.status === 'uploaded' ? '#7b1fa2' :
                             status.status === 'upload_failed' ? '#d32f2f' :
                             status.status === 'canceled' ? '#616161' : 'rgba(0,0,0,0.08)') :
                            (status.status === 'uploaded' ? 'rgba(156, 39, 176, 0.08)' :
                             status.status === 'upload_failed' ? 'rgba(244, 67, 54, 0.08)' :
                             status.status === 'canceled' ? 'rgba(117, 117, 117, 0.08)' : 'rgba(0,0,0,0.08)')
                        },
                        transition: 'transform 0.2s'
                      }}
                    />
                  </Box>
                ))}
              </Box>
              
              <Typography 
                variant="caption" 
                color="text.secondary"
                textAlign="center"
                sx={{ maxWidth: 160, mt: 1 }}
              >
                Processing finished with different outcomes
              </Typography>
            </Box>
          </Box>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mt: 2, fontStyle: 'italic' }}
          >
            Jobs progress through these states as they move through the reporter portal workflow.
            <strong> Click on any stage above to filter jobs by that workflow state.</strong>
          </Typography>
        </CardContent>
      </Card>
    );
  };

  const pageActions = (
    <Box>
      <Button
        startIcon={<FilterIcon />}
        onClick={() => setShowFilters(!showFilters)}
        sx={{ mr: 1 }}
      >
        Filters
      </Button>
      <Button
        startIcon={<RefreshIcon />}
        onClick={() => loadJobs()}
        variant="outlined"
      >
        Refresh
      </Button>
    </Box>
  );

  return (
    <Layout pageTitle="Jobs" pageActions={pageActions}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Showing jobs with activity in the last {dataPeriod.businessDays} business days
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            Data period: {dataPeriod.startDate} - {dataPeriod.endDate}
          </Typography><br/>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            Use filters below to narrow by date range, workflow state, or other criteria
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Workflow Diagram */}
        <WorkflowDiagram />

        {/* Filters Panel */}
        {showFilters && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Filters
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Workflow State</InputLabel>
                    <Select
                      multiple
                      value={filters.workflowState || []}
                      onChange={(e) => handleFilterChange('workflowState', e.target.value)}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as WorkflowState[]).map((value) => (
                            <Chip key={value} label={workflowStateLabels[value]} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {Object.entries(workflowStateLabels).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Completion Status</InputLabel>
                    <Select
                      multiple
                      value={filters.completionStatus || []}
                      onChange={(e) => handleFilterChange('completionStatus', e.target.value)}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as CompletionStatus[]).map((value) => (
                            <Chip key={value} label={completionStatusLabels[value]} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {Object.entries(completionStatusLabels).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="Start Date"
                    value={filters.dateRange?.startDate ? new Date(filters.dateRange.startDate) : null}
                    onChange={(date) => {
                      const dateRange = filters.dateRange || {};
                      handleFilterChange('dateRange', {
                        ...dateRange,
                        startDate: date ? date.toISOString().split('T')[0] : undefined,
                      });
                    }}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="End Date"
                    value={filters.dateRange?.endDate ? new Date(filters.dateRange.endDate) : null}
                    onChange={(date) => {
                      const dateRange = filters.dateRange || {};
                      handleFilterChange('dateRange', {
                        ...dateRange,
                        endDate: date ? date.toISOString().split('T')[0] : undefined,
                      });
                    }}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Job Number"
                    value={filters.jobNumber || ''}
                    onChange={(e) => handleFilterChange('jobNumber', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Reporter Name"
                    value={filters.reporterName || ''}
                    onChange={(e) => handleFilterChange('reporterName', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Case Name"
                    value={filters.caseName || ''}
                    onChange={(e) => handleFilterChange('caseName', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Firm Name"
                    value={filters.firmName || ''}
                    onChange={(e) => handleFilterChange('firmName', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button onClick={clearFilters} variant="outlined">
                    Clear Filters
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Jobs Data Grid */}
        <Card>
          <CardContent>
            {directLookup && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This job was found via direct lookup outside the recent activity window.
              </Alert>
            )}
            <DataGrid
              rows={jobs}
              columns={columns}
              getRowId={(row) => row.jobNumber}
              paginationModel={paginationModel}
              onPaginationModelChange={(model) => {
                lastInteractionRef.current = Date.now();
                setPaginationModel(model);
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              rowCount={totalCount}
              paginationMode="server"
              loading={loading}
              density="compact"
              sx={{ height: 600 }}
              disableRowSelectionOnClick
              disableDensitySelector
            />
          </CardContent>
        </Card>
      </LocalizationProvider>
    </Layout>
  );
};